import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Badge, Button, Card, ConfirmModal, Skeleton } from '@/components/ui';
import {
  useApproveSignup,
  useRejectSignup,
  useSignupRequests,
  type SignupRequest,
} from '@/hooks/useSignupRequests';
import { ApiError } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';

// ---------------------------------------------------------------------------
// Platform admin → review queue for public self-service sign-ups. Lives at
// /admin/signups; the sidebar shows the link only for tenant_type='platform'
// and the API rejects everyone else with 403. Approve provisions + activates
// the tenant from the pending request; reject declines it.
// ---------------------------------------------------------------------------
export default function AdminSignupsPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const list = useSignupRequests();
  const items = list.data?.items ?? [];

  const approve = useApproveSignup();
  const reject = useRejectSignup();

  const [approving, setApproving] = useState<SignupRequest | null>(null);
  const [rejecting, setRejecting] = useState<SignupRequest | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const errText = (err: unknown) =>
    err instanceof ApiError ? err.message : t('as_error');

  const doApprove = async () => {
    if (!approving) return;
    setErrorMsg(null);
    try {
      await approve.mutateAsync(approving.id);
      setApproving(null);
    } catch (err) {
      setErrorMsg(errText(err));
    }
  };

  const doReject = async () => {
    if (!rejecting) return;
    setErrorMsg(null);
    try {
      await reject.mutateAsync({ id: rejecting.id, note: rejectNote.trim() || undefined });
      setRejecting(null);
      setRejectNote('');
    } catch (err) {
      setErrorMsg(errText(err));
    }
  };

  return (
    <div className="px-8 py-8 max-w-[1100px] mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {t('as_title')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1.5">
            {items.length === 0
              ? t('as_sub')
              : t('as_count', { total: items.length.toLocaleString(lang) })}
          </p>
        </div>
      </header>

      {errorMsg && (
        <div
          role="alert"
          className="flex gap-2.5 items-start px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-5"
        >
          <Icon.warn width="18" height="18" className="text-danger flex-shrink-0 mt-px" />
          <div className="text-[13px] text-danger leading-relaxed">{errorMsg}</div>
        </div>
      )}

      <Card padding="none">
        {list.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <p className="p-8 text-center text-[13px] text-text-4">{t('as_error')}</p>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-11 h-11 rounded-full bg-volt-50 grid place-items-center mx-auto mb-4">
              <Icon.inbox width="20" height="20" className="text-volt-600" />
            </div>
            <p className="text-[14px] text-text-2 font-medium">{t('as_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1">{t('as_empty_sub')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line text-left text-[11px] uppercase tracking-[0.05em] text-text-4">
                  <th className="font-semibold px-5 py-3">{t('as_col_applicant')}</th>
                  <th className="font-semibold px-4 py-3">{t('as_col_company')}</th>
                  <th className="font-semibold px-4 py-3">{t('as_col_phone')}</th>
                  <th className="font-semibold px-4 py-3">{t('as_col_requested')}</th>
                  <th className="font-semibold px-5 py-3 text-right">{t('as_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-b border-line last:border-0 hover:bg-paper-2">
                    <td className="px-5 py-3.5 align-top">
                      <div className="font-medium text-text">{s.full_name}</div>
                      <a
                        href={`mailto:${s.email}`}
                        className="text-[12px] text-volt-600 hover:underline break-all"
                      >
                        {s.email}
                      </a>
                    </td>
                    <td className="px-4 py-3.5 align-top text-text-2">
                      {s.company || <span className="text-text-4">—</span>}
                      {s.country && (
                        <Badge className="ml-2 align-middle" variant="neutral">
                          {s.country}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3.5 align-top text-text-2 tabular-nums whitespace-nowrap">
                      {s.phone || <span className="text-text-4">—</span>}
                    </td>
                    <td className="px-4 py-3.5 align-top text-text-3 whitespace-nowrap">
                      {formatRelativeTime(s.created_at)}
                    </td>
                    <td className="px-5 py-3 align-top">
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setErrorMsg(null);
                            setRejecting(s);
                          }}
                        >
                          {t('as_reject')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setErrorMsg(null);
                            setApproving(s);
                          }}
                        >
                          {t('as_approve')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Approve — provisions + activates the tenant */}
      <ConfirmModal
        open={approving !== null}
        title={t('as_approve_title', { name: approving?.full_name ?? '' })}
        description={t('as_approve_desc', { email: approving?.email ?? '' })}
        confirmLabel={t('as_approve_confirm')}
        cancelLabel={t('as_cancel')}
        tone="primary"
        busy={approve.isPending}
        onConfirm={doApprove}
        onClose={() => {
          if (!approve.isPending) setApproving(null);
        }}
      />

      {/* Reject — with an optional internal note */}
      <ConfirmModal
        open={rejecting !== null}
        title={t('as_reject_title', { name: rejecting?.full_name ?? '' })}
        description={
          <div className="space-y-3">
            <p className="text-[13px] text-text-3 leading-relaxed m-0">{t('as_reject_desc')}</p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder={t('as_reject_note_ph')}
              rows={3}
              className="w-full text-[13px] rounded-md border border-line bg-paper px-3 py-2 text-text placeholder:text-text-4 focus:outline-none focus:ring-2 focus:ring-volt-200 focus:border-volt-400 resize-none"
            />
          </div>
        }
        confirmLabel={t('as_reject_confirm')}
        cancelLabel={t('as_cancel')}
        tone="danger"
        busy={reject.isPending}
        onConfirm={doReject}
        onClose={() => {
          if (!reject.isPending) {
            setRejecting(null);
            setRejectNote('');
          }
        }}
      />
    </div>
  );
}
